import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  PlusCircle, 
  History, 
  BarChart3, 
  Settings,
  LogOut,
  Eye,
  Images,
  Trophy,
  User,
  Users
} from 'lucide-react';
import { toast } from 'sonner';

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();

  const navItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/nueva-sesion', icon: PlusCircle, label: 'Nueva Sesión' },
    { path: '/consenso', icon: Users, label: 'Consenso' },
    { path: '/historial', icon: History, label: 'Historial' },
    { path: '/estadisticas', icon: BarChart3, label: 'Estadísticas' },
    { path: '/perfil', icon: User, label: 'Perfil' },
    { path: '#leaderboard', icon: Trophy, label: 'Leaderboard', comingSoon: true },
  ];

  const adminItems = [
    { path: '/admin', icon: Settings, label: 'Admin Panel' },
    { path: '/admin/images', icon: Images, label: 'Imágenes' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[hsl(232,36%,9%)] border-r border-[hsl(232,18%,18%)] flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-[hsl(232,18%,18%)]">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://academiacortex.com.ar/imagenes/iconBlanco.png" 
              alt="Logo" 
              className="w-10 h-10 object-contain"
            />
            <div>
              <h1 className="text-xl font-bold text-white">Oráculo Cortex</h1>
              <p className="text-xs text-white/50">Remote Viewing Training</p>
            </div>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            item.comingSoon ? (
              <button
                key={item.path}
                onClick={() => toast.info('Próximamente', { description: 'Esta función estará disponible pronto.' })}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors w-full text-left text-white/70 hover:bg-white/5 hover:text-white"
              >
                <item.icon className="w-5 h-5" />
                {item.label}
                <span className="ml-auto text-[10px] bg-[hsl(45,93%,47%)]/20 text-[hsl(45,93%,47%)] px-1.5 py-0.5 rounded">Pronto</span>
              </button>
            ) : (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive(item.path)
                    ? 'bg-[hsl(198,62%,54%)] text-white'
                    : 'text-white/70 hover:bg-white/5 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            )
          ))}

          {isAdmin && (
            <>
              <div className="pt-4 mt-4 border-t border-[hsl(232,18%,18%)]">
                <p className="px-4 py-2 text-xs uppercase text-white/40 tracking-wider">Admin</p>
              </div>
              {adminItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'bg-[hsl(252,37%,60%)] text-white'
                      : 'text-white/70 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              ))}
            </>
          )}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-[hsl(232,18%,18%)]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-[hsl(252,37%,60%)] flex items-center justify-center text-white font-medium">
              {user?.name?.[0]?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-white/50 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-white/70 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
